/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useContext } from "react"
import { Button, Divider, Flex, Typography, Modal } from "@duxton-web/ui"
import { ArrowRightUniversal, DownloadUniversal } from "@duxton-web/icons"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import UploadFailed from "pages/menu/detail/bulkUpload/bulkUploadNotifications/UploadFailed"
import { useSelector } from "react-redux"
import { isOngoingTaskFoundSelector } from "store/duck/bulkUpload/utils"
import { useTranslations } from "common/utils/i18n"
import { useScribeUploadAndPublishDrawer } from "pages/menu/detail/bulkUpload/scribe/useScribeUploadAndPublishDrawer"
import {
  HELP_CENTER_BULK_UPLOAD_INVENTORY_LINKS,
  HELP_CENTER_BULK_UPLOAD_MENU_LINKS,
  HELP_CENTER_BULK_UPLOAD_MART_MENU_LINKS,
  getHelpCenterBulkUploadLink,
} from "constants/menu_bulk_upload"
import { AppState } from "store/store.type"
import { ACTION_TYPE } from "types/delv-platform-api/inventoryCategory"
import BulkItemUI from "lib/bulk-editor-ui/components/BulkItem"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { storageHelper } from "utils/storage"
import { useUploadFileBehavior } from "../hooks/useUploadFileBehavior"
import FileDragNDrop from "./FileDragNDrop"
import DrawerFooter from "./DrawerFooter"
import EditorButtonWrapper from "./EditorButtonWrapper"
import ExportFileFailed from "../../bulkUploadNotifications/ExportFileFailed"
import { useExportFileButtonProps } from "../hooks/useExportFileButtonProps"
import {
  detailedInstuction,
  disabledDrawerBodyWrap,
  divider,
  drawerBodyWrap,
  drawerFooterWrap,
  orText,
  prepareStep,
  prepareTitle,
  bulkItemUI,
  modalContent,
} from "./styles"

const { Title } = Typography

const DrawerStartAndPublish = () => {
  const [
    downloadInventoryText,
    downloadCatalogueText,
    detailedInstructionsText,
    editInventoryDescText,
    createMenuDescText,
    editMenuDescText,
    step1TitleText,
    step2TitleTextInventory,
    step2TitleTextMenu,
    // step for update inventory
    downloadCurrentInventoryText,
    updateYourInventoryText,
    // step for create menu
    beginDownloadTemplateText,
    prepareItemPhotosText,
    compressFolderText,
    // step for update menu
    downloadExistingMenuText,
    updateYourItemsText,
    compressFolderBackText,
  ] = useTranslations([
    "bulkUpload.drawers.start.downloadInventory",
    "bulkUpload.drawers.start.downloadCatalogue",
    "bulkUpload.drawers.start.detailedInstructions",
    "bulkUpload.drawers.addItem.description",
    "bulkUpload.drawers.addItem.description",
    "bulkUpload.drawers.updateItem.description",
    "bulkUpload.drawers.step.step1",
    "bulkUpload.drawers.step.step2Inventory",
    "bulkUpload.drawers.step.step2Menu",
    "bulkUpload.drawers.step.downloadCurrentInventory",
    "bulkUpload.drawers.step.updateYourInventory",
    "bulkUpload.drawers.step.beginDownloadTemplate",
    "bulkUpload.drawers.step.prepareItemPhotos",
    "bulkUpload.drawers.step.compressFolder",
    "bulkUpload.drawers.step.downloadExistingMenu",
    "bulkUpload.drawers.step.updateYourItems",
    "bulkUpload.drawers.step.compressFolderBack",
  ])

  const [
    ,
    step1TextCreateMenuStart,
    step1TextCreateMenuLink,
    step1TextCreateMenuEnd,
  ] = (beginDownloadTemplateText.match(/(.*)<a href="link">(.*)<\/a>(.*)/) ||
    []) as RegExpMatchArray

  const getDescText = () => {
    if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY)
      return editInventoryDescText
    if (actionType === ACTION_TYPE.CREATE) return createMenuDescText
    return editMenuDescText
  }

  const {
    bulkUploadType,
    actionType,
    setDrawerState,
    modalEditorVisible,
    setModalEditorVisible,
  } = useContext(BulkUploadContext)

  const {
    selectedFileName,
    onSelectFileFromLocal,
    onUploadFileToServer,
    onDropFileFromLocal,
    uploadFileFailedError,
    clearUploadFileFailedError,
    isRtkFlagLoading,
  } = useUploadFileBehavior()
  const {
    trackDownloadCSVClick,
    trackPrepareBulkClick,
  } = useScribeUploadAndPublishDrawer()

  const isMartStore = useSelector((state: AppState) => state.menu.isMartStore)

  const onClickHelpCenter = () => {
    trackPrepareBulkClick()

    const country = storageHelper.getUserCountry() || "SG"
    const language = storageHelper.getLang() || "en"

    let links
    if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY) {
      links = HELP_CENTER_BULK_UPLOAD_INVENTORY_LINKS
    } else if (isMartStore) {
      links = HELP_CENTER_BULK_UPLOAD_MART_MENU_LINKS
    } else {
      links = HELP_CENTER_BULK_UPLOAD_MENU_LINKS
    }

    const helpCenterLink = getHelpCenterBulkUploadLink(links, country, language)
    window.open(helpCenterLink, "_blank")?.focus()
  }

  const isOngoingTaskFound = useSelector(isOngoingTaskFoundSelector)

  const {
    onClickExportFile,
    exportFileErrorPopupVisible,
    onCloseExportFileErrorPopup,
  } = useExportFileButtonProps()

  const handleClickDownload = () => {
    if (actionType === ACTION_TYPE.CREATE) {
      onClickExportFile()
      return
    }
    setDrawerState(DRAWER_STATES.DOWNLOAD_CATALOGUE)
    trackDownloadCSVClick()
  }

  const renderPrepareStep = () => {
    if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY)
      return (
        <ul css={prepareStep}>
          <li>
            {downloadCurrentInventoryText}
            <Button
              type="secondary"
              icon={<DownloadUniversal />}
              size="small"
              onClick={handleClickDownload}
            >
              {downloadInventoryText}
            </Button>
          </li>
          <li>{updateYourInventoryText}</li>
        </ul>
      )

    if (actionType === ACTION_TYPE.CREATE)
      return (
        <ul css={prepareStep}>
          <li>
            {step1TextCreateMenuStart}{" "}
            <span className="download" onClick={handleClickDownload}>
              {step1TextCreateMenuLink}
            </span>{" "}
            {step1TextCreateMenuEnd}
          </li>
          <li>{prepareItemPhotosText}</li>
          <li>{compressFolderText}</li>
        </ul>
      )

    return (
      <ul css={prepareStep}>
        <li>
          {downloadExistingMenuText}
          <Button
            type="secondary"
            icon={<DownloadUniversal />}
            size="small"
            onClick={handleClickDownload}
          >
            {downloadCatalogueText}
          </Button>
        </li>
        <li>{updateYourItemsText}</li>
        <li>{compressFolderBackText}</li>
      </ul>
    )
  }

  return (
    <div>
      <div>
        <div
          css={css`
            ${drawerBodyWrap};
            ${isOngoingTaskFound ? disabledDrawerBodyWrap : ""}
          `}
        >
          <p>{getDescText()}</p>
          <Title level={4} css={prepareTitle}>
            {step1TitleText}
          </Title>
          {renderPrepareStep()}
          <div css={detailedInstuction} onClick={onClickHelpCenter}>
            {detailedInstructionsText} <ArrowRightUniversal />{" "}
          </div>
          <Divider css={divider} />
          <Title level={4} css={prepareTitle}>
            {bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
              ? step2TitleTextInventory
              : step2TitleTextMenu}
          </Title>
          <FileDragNDrop
            selectedFileName={selectedFileName}
            onSelectFileFromLocal={onSelectFileFromLocal}
            onDropFileFromLocal={onDropFileFromLocal}
          />
          {/* only for create bulk upload for now, there is more flow for update */}
          {getGrabXValueFromLocalStorage(
            MerchantPortalFeatureFlag.enableEditorUIBulkUpload,
          ) &&
            actionType === ACTION_TYPE.CREATE && (
              <Flex
                gap={8}
                vertical
                css={css`
                  margin-top: 8px;
                `}
              >
                <div css={orText}>or</div>
                <EditorButtonWrapper />
              </Flex>
            )}
        </div>
        <div css={drawerFooterWrap}>
          <DrawerFooter
            onUploadFileToServer={onUploadFileToServer}
            isUploadFileDisabled={isRtkFlagLoading}
          />
        </div>
      </div>
      <UploadFailed
        visible={!!uploadFileFailedError?.isError}
        overwriteErrorContent={{
          message: uploadFileFailedError?.errorMessage,
        }}
        onCloseCallback={clearUploadFileFailedError}
      />
      <ExportFileFailed
        visible={exportFileErrorPopupVisible}
        onCloseCallback={onCloseExportFileErrorPopup}
      />
      <Modal
        open={modalEditorVisible}
        onCancel={() => setModalEditorVisible(false)}
        onOk={() => setModalEditorVisible(false)}
        closeIcon={null}
        width="auto"
        css={modalContent}
      >
        <div css={bulkItemUI}>
          <BulkItemUI />
        </div>
      </Modal>
    </div>
  )
}

export default DrawerStartAndPublish
